/** Fields in Customize mode (E3, spec 2026-09-18 §6): label and help are
 * copy; `type` / `options` / `optional` are contract and read locked.
 * SA-1060: a closed card whose line counts the fields and the restricted ones. */
import { Lock } from 'lucide-react'
import { Input } from '@/components/ui'
import type { ProcessWorkflowDefinitionJson } from '@/types/workflowDefinition'
import type { WorkflowOverlayJson } from '@/types/workflowConfig'
import { FORK_SENTENCE } from './LockedRow'
import { ProcessEditorCard } from './ProcessEditorCard'
import { FIELDS_CARD, type EditorCards } from './editorCards'
import { fieldsSummary } from './editorSummaries'

export function ProcessFieldsEditor({
  base,
  overlay,
  onText,
  cards,
  edited = false,
}: {
  base: ProcessWorkflowDefinitionJson
  overlay: WorkflowOverlayJson
  onText: (fieldId: string, key: 'label' | 'help', lang: string, text: string) => void
  /** SA-1060: the card's open state. */
  cards: EditorCards
  /** SA-1060: any overlay entry on a field (ruling Q3). */
  edited?: boolean
}) {
  const languages = base.languages?.length ? base.languages : ['en']
  return (
    <ProcessEditorCard
      cardId={FIELDS_CARD}
      title="Fields"
      label="Fields"
      summary={fieldsSummary(base)}
      edited={edited}
      open={cards.isOpen(FIELDS_CARD)}
      onOpenChange={(open) => cards.setOpen(FIELDS_CARD, open)}
    >
      <p className="text-xs text-muted-foreground">
        What staff read on the task form. A field's type is locked. {FORK_SENTENCE}
      </p>
      {Object.entries(base.fields).map(([id, field]) => {
        const name = field.label?.en ?? id
        const detail = field.options?.length ? field.options : field.columns ? Object.keys(field.columns) : []
        const contract = [field.type, detail.length ? `: ${detail.join(', ')}` : ''].join('')
        return (
          <div key={id} className="space-y-2 border-t border-border/60 pt-3 text-xs">
            <p className="flex items-center gap-1.5 text-muted-foreground">
              <Lock aria-label="locked" className="h-3 w-3 shrink-0" />
              <span className="font-mono">{id}</span>
              <span title={FORK_SENTENCE}>{contract}</span>
              {field.optional && <span>· optional</span>}
            </p>
            {languages.map((l) => {
              const suffix = l === languages[0] ? '' : ` (${l})`
              return (
                <div key={l} className="grid gap-3 sm:grid-cols-2">
                  <label className="block space-y-1 font-medium text-foreground">
                    <span>Label{suffix}</span>
                    <Input
                      aria-label={`Label for ${name}${suffix}`}
                      value={overlay.fields?.[id]?.label?.[l] ?? field.label?.[l] ?? ''}
                      onChange={(e) => onText(id, 'label', l, e.target.value)}
                    />
                  </label>
                  <label className="block space-y-1 font-medium text-foreground">
                    <span>Help{suffix}</span>
                    <Input
                      aria-label={`Help for ${name}${suffix}`}
                      value={overlay.fields?.[id]?.help?.[l] ?? field.help?.[l] ?? ''}
                      onChange={(e) => onText(id, 'help', l, e.target.value)}
                    />
                  </label>
                </div>
              )
            })}
          </div>
        )
      })}
    </ProcessEditorCard>
  )
}
